import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FirebaseAuthService } from '../services/firebaseAuthService';
import { Lock, AlertTriangle, Eye, EyeOff, CheckCircle } from 'lucide-react';

export function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Firebase recovery sends `oobCode` in query params
  const searchParams = new URLSearchParams(location.search);
  const oobCode = searchParams.get('oobCode') || '';

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    setIsLoading(true);

    try {
      if (oobCode) {
        // Redefinição via link de e-mail do Firebase
        await FirebaseAuthService.confirmResetPassword(oobCode, password);
      } else {
        // Redefinição do usuário com sessão ativa no Firebase
        await FirebaseAuthService.updateCurrentUserPassword(password);
      }
      
      setIsSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2500);
    } catch (err: any) {
      if (err?.code === 'auth/expired-action-code') {
        setError('O link de recuperação expirou. Solicite um novo link.');
      } else if (err?.code === 'auth/invalid-action-code') {
        setError('Código de recuperação inválido ou já utilizado.');
      } else {
        setError(err.message || 'Erro ao redefinir a senha no Firebase.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Redefinir Senha
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Digite sua nova senha de acesso ao Firebase.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {isSuccess ? (
            <div className="text-center py-4">
              <CheckCircle className="mx-auto h-12 w-12 text-emerald-500 mb-3" />
              <h3 className="text-lg font-bold text-gray-900 mb-1">Senha Atualizada!</h3>
              <p className="text-sm text-gray-600 mb-4">
                Sua senha foi redefinida com sucesso. Redirecionando para o login...
              </p>
              <Link
                to="/login"
                className="inline-flex items-center text-sm font-semibold text-red-600 hover:text-red-500"
              >
                Ir para o login agora
              </Link>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Nova Senha</label>
                <div className="mt-1 relative rounded-md shadow-sm group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-red-500" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 pr-10 sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                    placeholder="Nova senha (mínimo 6 dígitos)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Confirmar Nova Senha</label>
                <div className="mt-1 relative rounded-md shadow-sm group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-red-500" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={6}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 pr-10 sm:text-sm border-gray-300 rounded-md py-2 px-3 border"
                    placeholder="Confirme a nova senha"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {isLoading ? 'Salvando...' : 'Salvar nova senha no Firebase'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
